import React, { useContext } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ClearAll, Timer, Schedule } from '@mui/icons-material';
import { TimerContext } from '../contexts/TimerContext';

const StatsPage = () => {
  const { logs, clearLogs } = useContext(TimerContext);
  const [confirmDialog, setConfirmDialog] = React.useState({ open: false, action: null });

  const handleClearLogs = () => {
    setConfirmDialog({ 
      open: true, 
      action: () => {
        clearLogs();
        setConfirmDialog({ open: false, action: null });
      },
      title: 'Clear All Logs',
      message: 'Are you sure you want to clear all timer logs? This action cannot be undone.'
    });
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ open: false, action: null });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const getModeIcon = (mode) => {
    return mode === 'pomodoro' ? <Schedule /> : <Timer />;
  };

  const getModeColor = (mode) => {
    return mode === 'pomodoro' ? 'secondary' : 'primary';
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        Statistics
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Timer Logs ({logs.length} entries)
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ClearAll />}
            onClick={handleClearLogs}
            disabled={logs.length === 0}
          >
            Clear All Logs
          </Button>
        </Box>
        
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {logs.length === 0 ? (
            <Typography color="textSecondary">No logs available</Typography>
          ) : (
            <List dense>
              {logs.slice(-20).reverse().map((log) => (
                <ListItem key={log.id}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          icon={getModeIcon(log.mode || 'free')}
                          label={log.mode === 'pomodoro' ? 'Pomodoro' : 'Timer'}
                          size="small"
                          color={getModeColor(log.mode || 'free')}
                          variant="outlined"
                        />
                        <Typography variant="body1">
                          {log.action.toUpperCase()} - {log.task}
                        </Typography>
                      </Box>
                    }
                    secondary={formatDate(log.time)}
                  />
                </ListItem>
              ))}
              {logs.length > 20 && (
                <ListItem>
                  <ListItemText
                    primary={`... and ${logs.length - 20} more entries`}
                    secondary="Export data to see all logs"
                  />
                </ListItem>
              )}
            </List>
          )}
        </Box>
      </Paper>

      <Dialog open={confirmDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={confirmDialog.action} color="error" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StatsPage;